import { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { Section } from '@sorare/core/src/components/layout/MainAsideLayout';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Game, Props as GameProps } from 'components/seo/Games/Game';

const messages = defineMessages({
  nameUpcomingFixtures: {
    id: 'Seo.Matches.nameUpcomingFixtures',
    defaultMessage: '{displayName} upcoming fixtures',
  },
  nameUpcomingGames: {
    id: 'Seo.Matches.nameUpcomingGames',
    defaultMessage: '{displayName} upcoming games',
  },
  nameNextFixtures: {
    id: 'Seo.Matches.nameNextFixtures',
    defaultMessage: '{displayName} Next fixture',
  },
  nameNextGames: {
    id: 'Seo.Matches.nameNextGames',
    defaultMessage: '{displayName} Next game',
  },
  noFixtures: {
    id: 'seo.Club.Standings.noFixtures',
    defaultMessage: 'No fixtures for {displayName}',
  },
  noFutureFixtures: {
    id: 'seo.Club.Standings.noFutureFixtures',
    defaultMessage: 'No future fixtures for {displayName}',
  },
  noFutureGames: {
    id: 'seo.Club.Standings.noFutureGames',
    defaultMessage: 'No future games for {displayName}',
  },
});

const Root = styled(FullWidthContainer)`
  --game-height-approx: 120px;
  padding-bottom: var(--double-unit);
  padding-top: var(--double-unit);
`;

const GameWrapper = styled(Vertical)`
  @media ${desktopAndAbove} {
    &:last-child {
      position: sticky;
      bottom: var(--unit);
      margin-bottom: var(--double-unit);
      &:before {
        content: '';
        position: absolute;
        top: 0;
        width: 100%;
        height: 50px;
        background: linear-gradient(to bottom, transparent, var(--c-black) 50%);
        z-index: -1;
      }
    }
  }
`;

type Props = {
  displayName: string;
  loading: boolean;
  pastGames: GameProps['game'][];
  infiniteScrollLoader: ReactNode;
  futureGames: GameProps['game'][];
};

export const Matches = ({
  displayName,
  loading,
  pastGames,
  infiniteScrollLoader,
  futureGames,
}: Props) => {
  const { sport } = useSportContext();

  const noGames = !loading && !pastGames.length && !futureGames.length;

  if (noGames) {
    return (
      <Section topPadding>
        <FullWidthContainer>
          <Warning
            title={messages.noFixtures}
            variant="yellow"
            titleValues={{ displayName }}
          />
        </FullWidthContainer>
      </Section>
    );
  }

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <Root>
      <Vertical>
        {futureGames.length > 1 && (
          <HeadlineS>
            <FormattedMessage
              {...(sport === Sport.FOOTBALL
                ? messages.nameUpcomingFixtures
                : messages.nameUpcomingGames)}
              values={{ displayName }}
            />
          </HeadlineS>
        )}
        {!loading && !futureGames.length && (
          <Warning
            variant="yellow"
            title={
              sport === Sport.FOOTBALL
                ? messages.noFutureFixtures
                : messages.noFutureGames
            }
            titleValues={{ displayName }}
          />
        )}
        <Vertical>
          {futureGames.map((game, index) => (
            <GameWrapper key={game.id}>
              {futureGames.length === index + 1 ? (
                <HeadlineS>
                  {' '}
                  <FormattedMessage
                    {...(sport === Sport.FOOTBALL
                      ? messages.nameNextFixtures
                      : messages.nameNextGames)}
                    values={{ displayName }}
                  />
                </HeadlineS>
              ) : (
                ''
              )}
              <Game
                game={game}
                highlighted={futureGames.length === index + 1}
              />
            </GameWrapper>
          ))}
        </Vertical>
        {pastGames.map(game => (
          <Game key={game.id} game={game} />
        ))}
        {!loading && infiniteScrollLoader}
      </Vertical>
    </Root>
  );
};
